import React from 'react';
import { Box, Button, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ReplayIcon from '@mui/icons-material/Replay';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { personName, whomText, alsoText, pipelineSteps, failureInfo, cardIsRetryable, CALL_STATUS_TEXT, statusColor } from './callFormat';

dayjs.extend(relativeTime);

const STEP_COLOR = { done: 'success', active: 'info', failed: 'error', skipped: 'default', pending: 'default' };

/**
 * One call, as every admin sees it in the notification panel (plan §5.5):
 *   Grace → Matthew Lee's parent · 1723          Answered
 *   [Answered · 5:12] [Recording saved] [Transcript failed]
 *   transcription: rate limited   #42 ⧉
 */
const ActivityCard = React.memo(function ActivityCard({ card, onOpen, onRetry, dense = false }) {
    const steps = pipelineSteps(card);
    const failure = steps.find((s) => s.state === 'failed');
    const { unrecoverable: gaveUp } = failureInfo(card);
    const retryable = cardIsRetryable(card);
    const when = card.started_at || card.createdAt;
    const cn = card.student?.consultation_number;

    return (
        <Box
            onClick={onOpen ? () => onOpen(card) : undefined}
            sx={{
                p: dense ? 1 : 1.5, mb: 1, borderRadius: 2, border: '1px solid', borderColor: failure ? 'error.main' : 'divider',
                cursor: onOpen ? 'pointer' : 'default', '&:hover': onOpen ? { borderColor: 'primary.main' } : undefined,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant={dense ? 'body2' : 'body1'} sx={{ fontWeight: 600, flex: 1, minWidth: 0 }} noWrap>
                    {personName(card.admin) || 'Admin'} → {whomText(card)}{alsoText(card)}
                    {cn ? <Typography component="span" variant="caption" color="text.secondary"> · {cn}</Typography> : null}
                </Typography>
                <Chip size="small" label={CALL_STATUS_TEXT[card.status] || card.status} color={statusColor(card.status)} variant="outlined" />
            </Box>
            <Typography variant="caption" color="text.secondary">
                {when ? `${dayjs(when).format('MMM D, h:mm A')} · ${dayjs(when).fromNow()}` : ''}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
                {steps.map((s) => (
                    <Chip
                        key={s.key}
                        size="small"
                        label={s.label}
                        color={STEP_COLOR[s.state]}
                        variant={s.state === 'pending' || s.state === 'skipped' ? 'outlined' : 'filled'}
                        sx={{ opacity: s.state === 'pending' ? 0.6 : 1 }}
                    />
                ))}
            </Box>
            {failure && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
                    <Typography variant="caption" color="error" sx={{ flex: 1, wordBreak: 'break-word' }}>
                        {gaveUp ? 'Needs attention: ' : ''}{failure.detail || failure.label}
                    </Typography>
                    {onRetry && retryable && (
                        <Tooltip title={gaveUp ? 'Automatic retries have stopped. Retry now' : 'Retry now (otherwise retried nightly at 3:30)'}>
                            <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<ReplayIcon sx={{ fontSize: 14 }} />}
                                onClick={(e) => { e.stopPropagation(); onRetry(card); }}
                                sx={{ py: 0, minWidth: 0, textTransform: 'none', fontSize: 12 }}
                            >
                                Retry
                            </Button>
                        </Tooltip>
                    )}
                    <Typography variant="caption" color="text.secondary">#{card.id}</Typography>
                    <Tooltip title="Copy call id and error for a report">
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); navigator.clipboard?.writeText(`call_log #${card.id}: ${failure.detail || failure.label}`); }}
                        >
                            <ContentCopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}
        </Box>
    );
});

export default ActivityCard;
